import React from "react";
import { Card, Button, Tag, Avatar, Tooltip } from "antd";
import {
    MailOutlined,
    CopyOutlined,
    LoginOutlined,
    LoadingOutlined
} from "@ant-design/icons";
import { cn } from "../../../../utils/cn";
import { getInitials } from "../../../../utils/helper";

const EnhanceAffiliateCardView = ({
    record,
    actions = [],
    onCardClick,
    handleCopyLink,
    handleSendEmail,
    copyClipboardText,
    loadingStates,
}) => {
    // Separate Login action from others to place it in header
    const loginAction = actions.find(
        (action) => action.label === "Login As User" &&
            (!action.condition || action.condition(record))
    );

    // Filter other actions for bottom bar
    const filteredActions = actions
        .filter((action) => action.label !== "Login As User")
        .map((action) => {
            if (action.condition && !action.condition(record)) {
                return null;
            }
            if (action.transform) {
                return action.transform(record, action);
            }
            return action;
        })
        .filter(Boolean);

    const initials = getInitials(record.name)

    const getStripeStatusBadge = (status) => {
        if (status === "progress") return <Tag color="warning">Progress</Tag>;
        if (status === "completed") return <Tag color="success">Completed</Tag>;
        return <Tag>{status}</Tag>;
    };

    return (
        <Card
            hoverable
            className="h-full relative"
            onClick={() => onCardClick && onCardClick(record)}
        >
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 overflow-hidden">
                        <Avatar
                            size={48}
                            className="flex-shrink-0 font-semibold text-lg !bg-primary"
                        >
                            {initials}
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-base mb-1 truncate" title={record.name}>
                                {record.name || "N/A"}
                            </h3>
                            {record.companyName && (
                                <p className="text-gray-500 text-sm mb-0 truncate" title={record.companyName}>
                                    {record.companyName}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Login Button in Header */}
                    {loginAction && (
                        <Button
                            type="primary"
                            ghost
                            size="small"
                            icon={<LoginOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                loginAction.onClick(record);
                            }}
                            className="!border-primary !text-primary hover:!bg-primary hover:!text-white flex-shrink-0 ml-2
                            py-3"
                        >
                            Login As User
                        </Button>
                    )}
                </div>

                {/* Contact Info */}
                <div className="space-y-2 border-b border-gray-200 pb-3">
                    {record.email && (
                        <div className="flex items-center text-sm text-gray-600">
                            <MailOutlined className="mr-2 flex-shrink-0" />
                            <span className="truncate" title={record.email}>{record.email}</span>
                        </div>
                    )}
                    <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium mr-2">Type:</span>
                        <span className="capitalize">{record.userType || "-"}</span>
                    </div>
                </div>

                {/* Badges/Links Section */}
                <div className="space-y-4">
                    {/* Stripe Connected */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium">
                            Stripe Connected:
                        </span>
                        {getStripeStatusBadge(record.originalStripeStatus)}
                    </div>

                    {/* Onboarding Link */}
                    <div className="flex justify-between items-center w-full gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                            Onboarding Link:
                        </span>
                        <div>
                            {record.originalStripeStatus === "progress" ? (
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyLink(record.uniqueCode, "onboard-link");
                                        }}
                                        className={cn(
                                            "flex items-center hover:!border hover:!border-primary hover:!text-primary",
                                            copyClipboardText === record.uniqueCode ? "!border-green-500 !text-green-500" : ""
                                        )}
                                    >
                                        {copyClipboardText === record.uniqueCode ? "Copied" : "Copy"}
                                    </Button>
                                    <Button
                                        size="small"
                                        icon={loadingStates?.[record.id] ? <LoadingOutlined spin /> : <MailOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendEmail(record.id, "onboard-link");
                                        }}
                                        className="flex items-center hover:!border hover:!border-primary hover:!text-primary"
                                        disabled={loadingStates?.[record.id]}
                                    >
                                        Email
                                    </Button>
                                </div>
                            ) : (
                                <Tag color="red">Unavailable</Tag>
                            )}
                        </div>
                    </div>

                    {/* Ref. Link */}
                    <div className="flex justify-between items-center w-full gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                            Ref. Link:
                        </span>
                        <div>
                            {record.originalStripeStatus === "completed" ? (
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyLink(record.uniqueCode, "referral-link");
                                        }}
                                        className={cn(
                                            "flex items-center",
                                            copyClipboardText === record.uniqueCode ? "!border-green-500 !text-green-500" : ""
                                        )}
                                    >
                                        {copyClipboardText === record.uniqueCode ? "Copied" : "Copy"}
                                    </Button>
                                    <Button
                                        size="small"
                                        icon={loadingStates?.[record.id] ? <LoadingOutlined spin /> : <MailOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendEmail(record.id, "referral-link");
                                        }}
                                        disabled={loadingStates?.[record.id]}
                                    >
                                        Email
                                    </Button>
                                </div>
                            ) : (
                                <Tag color="red">Unavailable</Tag>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                {filteredActions.length > 0 && (
                    <div
                        className={cn(
                            "grid grid-cols-2 gap-2 pt-3 border-t border-gray-200 mt-2",
                            filteredActions.length === 1 && "flex justify-end"
                        )}
                    >
                        {filteredActions.map((action, idx) => (
                            <Button
                                key={idx}
                                title={action.title ?? action.label}
                                className={cn(
                                    "text-sm font-medium h-9 my-1 !min-w-full flex justify-center items-center transition-colors duration-150 ease-in-out",
                                    action.className,
                                    filteredActions.length === 1 && "!w-1/2"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(record);
                                }}
                            >
                                {action.icon &&
                                    React.cloneElement(action.icon, { className: "mr-2" })}
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </Card >
    );
};

export default EnhanceAffiliateCardView;